import React, { useEffect, useState } from "react";
import {
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Box,
  Chip,
  Tooltip,
  ButtonGroup,
} from "@mui/material";
import {
  Visibility,
  Delete,
  Edit,
  Search,
  Add,
  LocalHospital,
  CheckCircle,
  Warning,
} from "@mui/icons-material";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  listBeds,
  createBed,
  updateBed,
  deleteBed,
  listRooms,
  listWards,
} from "../../../api/bedManagement";

const BedList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalBeds, setTotalBeds] = useState(0);

  // Filter States
  const [wardFilter, setWardFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roomTypeFilter, setRoomTypeFilter] = useState("");

  // Debounce search query to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [wardFilter, roomTypeFilter, statusFilter]);

  // Fetch Beds with Pagination
  const { data, refetch, isLoading, isError } = useQuery({
    queryKey: [
      "beds-list",
      { search: debouncedSearch, page, page_size: rowsPerPage, room__ward: wardFilter, room__room_type: roomTypeFilter, status: statusFilter },
    ],
    queryFn: () =>
      listBeds({ 
        search: debouncedSearch || undefined, 
        page, 
        page_size: rowsPerPage,
        room__ward: wardFilter || undefined,
        room__room_type: roomTypeFilter || undefined,
        status: statusFilter || undefined,
      }, token),
  });

  // Fetch Wards for ward filter dropdown - optimized
  const { data: wardsData } = useQuery({
    queryKey: ["wards-dropdown"],
    queryFn: () => listWards({ page_size: 100 }, token),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch Rooms for dropdown in add/edit dialog - only fetch first 100 for dropdown (optimized)
  const { data: roomsData } = useQuery({
    queryKey: ["rooms-dropdown"],
    queryFn: () => listRooms({ page_size: 100 }, token),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Calculate total beds from response
  useEffect(() => {
    if (data) {
      const total = Array.isArray(data) ? data.length : data?.count || 0;
      setTotalBeds(total);
    }
  }, [data]);

  // Dialog and Snackbar States
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedBed, setSelectedBed] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [form, setForm] = useState({
    bed_number: "",
    room: "",
    status: 1,
  });

  // Handlers for Update Dialog
  const handleOpenUpdateDialog = (bed) => {
    setSelectedBed(bed);
    setForm({
      bed_number: bed ? bed.bed_number : "",
      room: bed ? bed.room : "",
      status: bed ? bed.status : 1,
    });
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedBed(null);
    setForm({ bed_number: "", room: "", status: 1 });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedBed) {
        await updateBed(selectedBed.id, form, token);
        setSnackbarMessage(
          t("bedManagement.bed_updated", {
            defaultValue: "Bed updated successfully",
          })
        );
      } else {
        await createBed(form, token);
        setSnackbarMessage(
          t("bedManagement.bed_created", {
            defaultValue: "Bed created successfully",
          })
        );
      }
      refetch();
      handleCloseUpdateDialog();
    } catch (error) {
      console.error("Error saving bed:", error);
      setSnackbarMessage(
        t("bedManagement.error_saving", { defaultValue: "Error saving bed" })
      );
    }
  };

  const handleDelete = async (bedId) => {
    try {
      await deleteBed(bedId, token);
      setSnackbarMessage(
        t("bedManagement.bed_deleted", {
          defaultValue: "Bed deleted successfully",
        })
      );
      refetch();
    } catch (error) {
      console.error("Error deleting bed:", error);
      setSnackbarMessage(
        t("bedManagement.error_deleting", {
          defaultValue: "Error deleting bed",
        })
      );
    }
  };

  // Quick status change
  const handleQuickStatusChange = async (bedId, newStatus) => {
    try {
      await updateBed(bedId, { status: newStatus }, token);
      setSnackbarMessage(
        t("bedManagement.bed_status_updated", {
          defaultValue: "Bed status updated successfully",
        })
      );
      refetch();
    } catch (error) {
      console.error("Error updating bed status:", error);
      setSnackbarMessage(
        t("bedManagement.error_updating", {
          defaultValue: "Error updating bed status",
        })
      );
    }
  };

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setTotalBeds(data.length);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  const getBedStatusLabel = (status) => {
    switch (status) {
      case 1:
        return t("bedManagement.available", { defaultValue: "Available" });
      case 2:
        return t("bedManagement.occupied", { defaultValue: "Occupied" });
      case 3:
        return t("bedManagement.cleaning", { defaultValue: "Cleaning" });
      case 4:
        return t("bedManagement.maintenance", { defaultValue: "Maintenance" });
      default:
        return "";
    }
  };

  const getBedStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success";
      case 2:
        return "error";
      case 3:
        return "warning";
      case 4:
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "none", m: "1em 3em" }}>
        <Grid container padding={"20px"}>
          {/* Header Section with Search Bar */}
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {t("bedManagement.beds.title", {
                defaultValue: "Beds Management",
              })}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenUpdateDialog(null)}
                sx={{ textTransform: "capitalize" }}
              >
                {t("bedManagement.add_bed", { defaultValue: "Add Bed" })}
              </Button>
              <TextField
                placeholder={t("bedManagement.search", {
                  defaultValue: "Search beds...",
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "300px" }}
              />
            </Box>
          </Grid>

          {/* Enhanced Filters */}
          <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={wardFilter}
                  onChange={(e) => setWardFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    {t("bedManagement.all_wards", {
                      defaultValue: "All Wards",
                    })}
                  </MenuItem>
                  {(Array.isArray(wardsData) ? wardsData : wardsData?.results || [])?.map((ward) => (
                    <MenuItem key={ward.id} value={ward.id}>
                      {ward.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    {t("bedManagement.all_status", {
                      defaultValue: "All Status",
                    })}
                  </MenuItem>
                  <MenuItem value={1}>
                    {t("bedManagement.available", {
                      defaultValue: "Available",
                    })}
                  </MenuItem>
                  <MenuItem value={2}>
                    {t("bedManagement.occupied", { defaultValue: "Occupied" })}
                  </MenuItem>
                  <MenuItem value={3}>
                    {t("bedManagement.cleaning", { defaultValue: "Cleaning" })}
                  </MenuItem>
                  <MenuItem value={4}>
                    {t("bedManagement.maintenance", {
                      defaultValue: "Maintenance",
                    })}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={roomTypeFilter}
                  onChange={(e) => setRoomTypeFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    {t("bedManagement.all_types", {
                      defaultValue: "All Room Types",
                    })}
                  </MenuItem>
                  <MenuItem value={1}>
                    {t("bedManagement.general", { defaultValue: "General" })}
                  </MenuItem>
                  <MenuItem value={2}>
                    {t("bedManagement.private", { defaultValue: "Private" })}
                  </MenuItem>
                  <MenuItem value={3}>
                    {t("bedManagement.vip", { defaultValue: "VIP" })}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <ButtonGroup variant="outlined" size="small">
                <Button onClick={() => setStatusFilter(1)}>
                  {t("bedManagement.show_available", {
                    defaultValue: "Show Available",
                  })}
                </Button>
                <Button onClick={() => setStatusFilter(2)}>
                  {t("bedManagement.show_occupied", {
                    defaultValue: "Show Occupied",
                  })}
                </Button>
                <Button onClick={() => setStatusFilter("")}>
                  {t("bedManagement.show_all", { defaultValue: "Show All" })}
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
          <Divider sx={{ mt: 1, width: "100%" }} />

          {/* Loading, Error, or Data Display */}
          {isLoading ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Grid>
          ) : isError ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <Typography color="error">
                {t("bedManagement.failedToLoad", {
                  defaultValue: "Failed to load beds",
                })}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => refetch()}
                sx={{ mt: 2 }}
              >
                {t("bedManagement.retry", { defaultValue: "Retry" })}
              </Button>
            </Grid>
          ) : (
            <>
              <TableContainer sx={{ mt: 4 }}>
                <Table sx={{ minWidth: "100%" }} aria-label="beds table">
                  <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                    <TableRow>
                      <TableCell align="center">
                        {t("bedManagement.beds.id", { defaultValue: "ID" })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.beds.bed_number", {
                          defaultValue: "Bed Number",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.beds.room", {
                          defaultValue: "Room",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.beds.ward", {
                          defaultValue: "Ward",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.beds.status", {
                          defaultValue: "Status",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.actions", {
                          defaultValue: "Actions",
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {(Array.isArray(data) ? data : data?.results || [])?.length > 0 ? (
                      (Array.isArray(data) ? data : data?.results || []).map((bed) => (
                        <TableRow
                          key={bed.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center">{bed.id}</TableCell>
                          <TableCell align="center">{bed.bed_number}</TableCell>
                          <TableCell align="center">
                            {bed.room_number}
                          </TableCell>
                          <TableCell align="center">{bed.ward_name}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getBedStatusLabel(bed.status)}
                              color={getBedStatusColor(bed.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              {/* Quick Status Actions */}
                              {bed.status === 1 && (
                                <Tooltip
                                  title={t("bedManagement.mark_occupied", {
                                    defaultValue: "Mark as Occupied",
                                  })}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleQuickStatusChange(bed.id, 2)
                                    }
                                    color="error"
                                  >
                                    <LocalHospital />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {bed.status === 2 && (
                                <Tooltip
                                  title={t("bedManagement.mark_available", {
                                    defaultValue: "Mark as Available",
                                  })}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleQuickStatusChange(bed.id, 1)
                                    }
                                    color="success"
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {bed.status === 2 && (
                                <Tooltip
                                  title={t("bedManagement.mark_cleaning", {
                                    defaultValue: "Mark for Cleaning",
                                  })}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleQuickStatusChange(bed.id, 3)
                                    }
                                    color="warning"
                                  >
                                    <Warning />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {bed.status === 3 && (
                                <Tooltip
                                  title={t("bedManagement.mark_available", {
                                    defaultValue: "Mark as Available",
                                  })}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleQuickStatusChange(bed.id, 1)
                                    }
                                    color="success"
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* Edit and Delete */}
                              <Tooltip
                                title={t("common.edit", {
                                  defaultValue: "Edit",
                                })}
                              >
                                <IconButton
                                  onClick={() => handleOpenUpdateDialog(bed)}
                                  color="primary"
                                  size="small"
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={t("common.delete", {
                                  defaultValue: "Delete",
                                })}
                              >
                                <IconButton
                                  onClick={() => handleDelete(bed.id)}
                                  color="error"
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t("bedManagement.no_beds", {
                              defaultValue: "No beds found",
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={3}
                flexWrap="wrap"
                gap={2}
              >
                {/* Rows Per Page Selector */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    {t("bedManagement.rows_per_page", "Rows Per Page")}:
                  </Typography>
                  <FormControl variant="standard" sx={{ minWidth: 80 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Pagination Component */}
                {totalBeds > rowsPerPage && (
                  <Pagination
                    count={Math.ceil(totalBeds / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                )}
              </Box>
            </>
          )}
        </Grid>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedBed
            ? t("bedManagement.beds.edit", { defaultValue: "Edit Bed" })
            : t("bedManagement.beds.add", { defaultValue: "Add Bed" })}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bedManagement.beds.bed_number", {
                    defaultValue: "Bed Number",
                  })}
                  value={form.bed_number}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, bed_number: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    value={form.room}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, room: e.target.value }))
                    }
                    displayEmpty
                    required
                  >
                    <MenuItem value="" disabled>
                      {t("bedManagement.select_room", {
                        defaultValue: "Select Room",
                      })}
                    </MenuItem>
                    {(Array.isArray(roomsData) ? roomsData : roomsData?.results || [])?.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.room_number} ({room.ward_name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value }))
                    }
                    required
                  >
                    <MenuItem value={1}>
                      {t("bedManagement.available", {
                        defaultValue: "Available",
                      })}
                    </MenuItem>
                    <MenuItem value={2}>
                      {t("bedManagement.occupied", {
                        defaultValue: "Occupied",
                      })}
                    </MenuItem>
                    <MenuItem value={3}>
                      {t("bedManagement.cleaning", {
                        defaultValue: "Cleaning",
                      })}
                    </MenuItem>
                    <MenuItem value={4}>
                      {t("bedManagement.maintenance", {
                        defaultValue: "Maintenance",
                      })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUpdateDialog}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" variant="contained">
              {selectedBed
                ? t("common.update", { defaultValue: "Update" })
                : t("common.create", { defaultValue: "Create" })}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
    </Grid>
  );
};

export default BedList;
